import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
  perPage?: number;
  onPerPageChange?: (perPage: number) => void;
  totalCount?: number;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  loading,
  perPage,
  onPerPageChange,
  totalCount
}) => {
  const generatePageNumbers = () => {
    const pages = [];

    if (totalPages <= 4) {
      // If total pages is 4 or less, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const midPoint = Math.ceil(totalPages / 2);

      if (currentPage <= midPoint) {
        // First half: show pages on the left side of ellipsis
        // Calculate the start page for the 3-page group
        let startPage = Math.max(1, currentPage - 1);

        // Ensure we don't go too far right in the first half
        if (startPage > midPoint - 1) {
          startPage = Math.max(1, midPoint - 1);
        }

        // Show 3 consecutive pages starting from startPage
        for (let i = 0; i < 3 && startPage + i <= totalPages; i++) {
          pages.push(startPage + i);
        }

        // Add ellipsis if there's a gap before the last page
        if (startPage + 2 < totalPages - 1) {
          pages.push('...');
        }

        // Add last page if it's not already included
        if (startPage + 2 < totalPages) {
          pages.push(totalPages);
        }
      } else {
        // Second half: show first page, ellipsis, then 3 pages on the right
        // Add first page
        pages.push(1);

        // Add ellipsis
        pages.push('...');

        // Calculate the start page for the last 3 pages
        let startPage = Math.max(1, currentPage - 1);

        // Ensure we show the last 3 pages when near the end
        if (startPage + 2 >= totalPages) {
          startPage = Math.max(1, totalPages - 2);
        }

        // Show 3 consecutive pages starting from startPage
        for (let i = 0; i < 3 && startPage + i <= totalPages; i++) {
          pages.push(startPage + i);
        }
      }
    }

    return pages;
  };

  const handleFirst = () => {
    onPageChange(1);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handleLast = () => {
    onPageChange(totalPages);
  };

  const handlePageClick = (page: number | string) => {
    if (typeof page === 'number') {
      onPageChange(page);
    }
  };

  const pageNumbers = generatePageNumbers();
  const showPagination = totalPages > 1;
  const showPerPageSelector = perPage && onPerPageChange && totalCount !== undefined;

  // Don't render anything if no pagination and no per-page selector
  if (!showPagination && !showPerPageSelector) {
    return null;
  }

  return (
    <div className="flex items-center justify-between gap-2">
      {/* left - Per Page Selector */}
      <div className="flex items-center gap-2 text-sm text-gray-600">
        {showPerPageSelector && (
          <>
            <span>Show</span>
            <Select
              value={perPage.toString()}
              onValueChange={(value) => onPerPageChange(parseInt(value))}
              disabled={loading}
            >
              <SelectTrigger className="w-[70px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="75">75</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span>of {totalCount} total entries</span>
          </>
        )}
      </div>

      {/* right */}
      <div className="flex items-center gap-2">
        {showPagination && (
          <>
            {/* First Page Button */}
            <button
              onClick={handleFirst}
              disabled={currentPage === 1 || loading}
              title={loading ? 'Loading...' : 'Go to first page'}
              className={`w-7 h-7 flex items-center justify-center bg-[#E9ECEF] rounded-lg text-sm font-medium border transition-colors ${
                currentPage === 1 || loading ? 'opacity-60 cursor-not-allowed' : ' border-gray-300 hover:bg-gray-50'
              }`}
            >
              <ChevronsLeft size={16} />
            </button>

            {/* Previous Button */}
            <button
              onClick={handlePrevious}
              disabled={currentPage === 1 || loading}
              title={loading ? 'Loading...' : 'Go to previous page'}
              className={`w-7 h-7 flex items-center justify-center bg-[#E9ECEF] rounded-lg text-sm font-medium border transition-colors ${
                currentPage === 1 || loading ? 'opacity-60 cursor-not-allowed' : ' border-gray-300 hover:bg-gray-50'
              }`}
            >
              <ChevronLeft size={16} />
            </button>

            {/* Page Numbers */}
            <div className="flex items-center gap-1">
              {pageNumbers.map((page, index) => (
                <React.Fragment key={index}>
                  {page === '...' ? (
                    <span className="px-3 py-2 text-gray-400">...</span>
                  ) : (
                    <button
                      onClick={() => handlePageClick(page)}
                      disabled={loading}
                      title={loading ? 'Loading...' : `Go to page ${page}`}
                      className={`w-7 h-7 bg-[#E9ECEF] flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                        currentPage === page
                          ? 'bg-primary text-white'
                          : loading
                            ? 'opacity-60 cursor-not-allowed'
                            : ' hover:bg-gray-50 border border-gray-300'
                      }`}
                    >
                      {page}
                    </button>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Next Button */}
            <button
              onClick={handleNext}
              disabled={currentPage === totalPages || loading}
              title={loading ? 'Loading...' : 'Go to next page'}
              className={`w-7 h-7 flex items-center justify-center bg-[#E9ECEF] rounded-lg text-sm font-medium border transition-colors ${
                currentPage === totalPages || loading
                  ? 'opacity-60 cursor-not-allowed'
                  : ' border-gray-300 hover:bg-gray-50'
              }`}
            >
              <ChevronRight size={16} />
            </button>

            {/* Last Page Button */}
            <button
              onClick={handleLast}
              disabled={currentPage === totalPages || loading}
              title={loading ? 'Loading...' : 'Go to last page'}
              className={`w-7 h-7 flex items-center justify-center bg-[#E9ECEF] rounded-lg text-sm font-medium border transition-colors ${
                currentPage === totalPages || loading
                  ? 'opacity-60 cursor-not-allowed'
                  : ' border-gray-300 hover:bg-gray-50'
              }`}
            >
              <ChevronsRight size={16} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Pagination;
